import React, { useEffect, useRef, useMemo, useCallback } from "react";
import { SimpleLoader } from "./sub/Loaders";
import TopPlanElement from "./TopPlanElement";
import ParentPlanGroup from "./ParentPlanGroup";

function TopPlanTreeCards(props) {
  const BottomRef = useRef(null);
  const propsRef = useRef(props);

  // Keep props ref updated
  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  // Create observer only once with stable callback
  useEffect(() => {
    if (!BottomRef.current) return;

    let isLoading = false;

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        const currentProps = propsRef.current;
        
        if (
          entry.isIntersecting &&
          !isLoading &&
          !currentProps.loading &&
          currentProps.totalTopPlanCount > currentProps.data.length
        ) {
          isLoading = true;
          const nextStartAt = currentProps.data.length;
          currentProps.setStartAt(nextStartAt);
          currentProps.loadMore(nextStartAt);
          
          // Reset loading flag after a delay to allow data to update
          setTimeout(() => {
            isLoading = false;
          }, 500);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: "50px",
      threshold: 0.1,
    });

    observer.observe(BottomRef.current);

    return () => {
      observer.disconnect();
    };
  }, []); // Only create once, never recreate

  // Group plans by parent while maintaining order
  const groupedPlans = useMemo(() => {
    const groups = {};
    const groupOrder = []; // Track order of parent groups
    const standalonePlans = [];

    if (!props.data || props.data.length === 0) {
      return { groups: [], standalonePlans };
    }

    props.data.forEach((plan) => {
      if (plan.cascadeFrom && plan.cascadeFrom.id) {
        // This plan has a parent
        const parentId = plan.cascadeFrom.id;
        if (!groups[parentId]) {
          groups[parentId] = {
            parentPlan: {
              id: parentId,
              title: plan.cascadeFrom.title,
              performance: plan.cascadeFrom.performance || 0,
              planGroup: plan.planGroup,
              progressStatus:
                (plan.cascadeFrom.performance || 0) >= 70
                  ? "On Track"
                  : (plan.cascadeFrom.performance || 0) >= 40
                  ? "Off Track"
                  : "At Risk",
            },
            childPlans: [],
          };
          groupOrder.push(parentId); // Track the order
        }
        groups[parentId].childPlans.push(plan);
      } else {
        // Standalone plan (no parent)
        standalonePlans.push(plan);
      }
    });

    // Sort child plans by hierarchyOrder within each group
    groupOrder.forEach(parentId => {
      groups[parentId].childPlans.sort((a, b) => {
        const orderA = a.hierarchyOrder !== null && a.hierarchyOrder !== undefined ? a.hierarchyOrder : 999999;
        const orderB = b.hierarchyOrder !== null && b.hierarchyOrder !== undefined ? b.hierarchyOrder : 999999;
        return orderA - orderB;
      });
    });

    // Return groups in the order they were encountered
    const orderedGroups = groupOrder.map(id => groups[id]);
    return { groups: orderedGroups, standalonePlans };
  }, [props.data]);

  // If no data, return empty
  if (!props.data || props.data.length === 0) {
    return props.loading ? <SimpleLoader /> : null;
  }

  return (
    <>
      {/* Render parent plan groups */}
      {groupedPlans.groups.map((group) => (
        <ParentPlanGroup
          key={group.parentPlan.id}
          parentPlan={group.parentPlan}
          childPlans={group.childPlans}
          allPlans={props.data}
          handleDelete={props.handleDelete}
          handleEdit={props.handleEdit}
          onDataUpdate={props.onDataUpdate}
        />
      ))}

      {/* Render standalone plans */}
      {groupedPlans.standalonePlans.map((element) => (
        <TopPlanElement
          key={element.planId}
          plans={props.data}
          data={element}
          handleDelete={props.handleDelete}
          handleEdit={props.handleEdit}
          onDataUpdate={props.onDataUpdate}
        />
      ))}

      {/* Bottom ref for infinite scroll */}
      <div ref={BottomRef} />

      {props.loading && <SimpleLoader />}
    </>
  );
}

export default TopPlanTreeCards;
